// ./src/components/features/MarketingChannels/MarketingChannelEditDrawer/MarketingChannelEditDrawer.tsx
//
// Create/edit drawer for an account-level marketing channel. Self-loading in edit
// mode via recordId; context-or-props resolution; no success toast (the page
// highlight is the confirmation). Fields: name, marker (the ?ch= code),
// description, status. The marker is normalized to a url-safe token server-side;
// a live preview shows what will land in the URL. See em-scaffold-ui.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, TextInput, Textarea, Text, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";

import { MarketingChannel, MarketingChannelInput } from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalMarketingChannelsContext } from "@/contexts/MarketingChannelsContext";
import { AppDrawer } from "@/components/generic";

export interface MarketingChannelEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** Pre-fill the name when opening in add mode. */
  initialName?: string | null;
  onCreated?: (channel: MarketingChannel) => void;
  onUpdated?: (channel: MarketingChannel) => void;
}

interface FormData {
  name: string;
  code: string;
  description: string;
}

const KNOWN_FORM_FIELDS = ["name", "code", "description"];

const getInitialFormData = (): FormData => ({
  name: "",
  code: "",
  description: "",
});

const fromRecord = (record: MarketingChannel): FormData => ({
  name: record.name || "",
  code: record.code || "",
  description: record.description || "",
});

const serialize = (data: FormData): string => JSON.stringify(data);

const MarketingChannelEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={90} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const MarketingChannelEditDrawer: React.FC<MarketingChannelEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  initialName,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["marketingChannels", "common"]);

  const ctx = useOptionalMarketingChannelsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<MarketingChannel | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as MarketingChannel);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record
      ? fromRecord(record)
      : { ...getInitialFormData(), name: initialName?.trim() || "" };
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, initialName]);

  const setField = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const submitData: MarketingChannelInput = {
      name: formData.name.trim() || null,
      code: formData.code.trim() || null,
      description: formData.description.trim() || null,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as MarketingChannel[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("marketingChannels:form.editTitle", { defaultValue: "Edit channel" })
    : t("marketingChannels:form.addTitle", { defaultValue: "Add channel" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <MarketingChannelEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("marketingChannels:form.fields.name.label", { defaultValue: "Name" })}
            description={t("marketingChannels:form.fields.name.description", {
              defaultValue: "What this channel is called, e.g. \"Facebook\" or \"Paper magazine\".",
            })}
            placeholder={t("marketingChannels:form.fields.name.placeholder", { defaultValue: "e.g. Facebook" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            data-autofocus
            size="md"
          />

          <TextInput
            label={t("marketingChannels:form.fields.code.label", { defaultValue: "Marker" })}
            description={t("marketingChannels:form.fields.code.description", {
              defaultValue: "The tag added to event links — short and url-safe, e.g. fb, insta, poster1.",
            })}
            placeholder={t("marketingChannels:form.fields.code.placeholder", { defaultValue: "e.g. fb" })}
            value={formData.code}
            onChange={(e) => setField("code", e.currentTarget.value)}
            error={fieldErrors.code}
            leftSection={
              <Text size="md" c="dimmed">
                ?ch=
              </Text>
            }
            leftSectionWidth={52}
            size="md"
          />

          <Textarea
            label={t("marketingChannels:form.fields.description.label", { defaultValue: "Description" })}
            placeholder={t("marketingChannels:form.fields.description.placeholder", {
              defaultValue: "Optional — a short note about where this channel is used.",
            })}
            value={formData.description}
            onChange={(e) => setField("description", e.currentTarget.value)}
            error={fieldErrors.description}
            autosize
            minRows={2}
            maxRows={4}
            size="md"
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
